import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup, Grid, IconButton, makeStyles, Typography } from "@material-ui/core";
import { CustomTextField } from "@totalsoft/rocket-webapp-components-deprecated";
import { get, sequence, set } from "@totalsoft/change-tracking-react";
import { onLensSliderChange, onNumericTextFieldChange } from "utils/propertyChangeAdapters";
import { getErrors, isValid } from "@totalsoft/pure-validations-react";
import DeleteSweepRoundedIcon from "@material-ui/icons/DeleteSweepRounded";
import productPageStyle from "features/products/styles";
import SimpleSliderWrapper from "../SimpleSliderWrapper";

const useStyles = makeStyles(productPageStyle);

function ServiceComponent(props) {
  const { serviceLens, settingsLens, currencyCode, serviceValidation, onDeleteService } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const serviceSettingsLens = useMemo(
    () =>
      (settingsLens.quoteProductServices |> sequence).find(
        (lens) => (lens.serviceTypeId |> get) === (serviceLens.serviceTypeId |> get)
      ),
    [settingsLens.quoteProductServices, serviceLens.serviceTypeId]
  );

  const serviceSettings = useMemo(() => (serviceSettingsLens ? serviceSettingsLens |> get : null), [
    serviceSettingsLens,
  ]);

  const handleUsePercent = useCallback(
    (useServicePercent) => () => {
      if (serviceSettingsLens) {
        (serviceSettingsLens.useServicePercent |> set)(useServicePercent);
      } else {
        const currentQuoteProductServices = settingsLens.quoteProductServices |> get;
        const newQuoteProductServices = [
          ...currentQuoteProductServices,
          { serviceTypeId: serviceLens.serviceTypeId |> get, useServicePercent },
        ];
        (settingsLens.quoteProductServices |> set)(newQuoteProductServices);
      }
    },
    [serviceSettingsLens, settingsLens.quoteProductServices, serviceLens.serviceTypeId]
  );

  const handleDeleteService = useCallback(() => {
    onDeleteService(serviceLens.serviceTypeId |> get);
  }, [onDeleteService, serviceLens.serviceTypeId]);

  return (
    <Grid container spacing={2} alignItems="center" alignContent="center">
      {serviceSettings?.useServicePercent && (
        <Grid className={classes.customSlider} item xs={7}>
          <SimpleSliderWrapper
            fullWidth
            min={serviceSettings.serviceMinPercent}
            max={serviceSettings.serviceMaxPercent}
            showSliderLimits={true}
            onChange={onLensSliderChange(serviceLens.servicePercent)}
            decimalScale={2}
            step={0.01}
            error={!isValid(serviceValidation?.servicePercent)}
            helperText={getErrors(serviceValidation?.servicePercent)}
            label={
              (serviceLens.serviceTypeName |> get) +
              " | " +
              t("DECIMAL2", { number: serviceLens.servicePercent |> get }) +
              " " +
              t("General.Percent") +
              " (" +
              currencyCode +
              " " +
              t("DECIMAL2", { number: serviceLens?.serviceAmount |> get }) +
              ")"
            }
            value={serviceLens.servicePercent |> get}
            disabled={Boolean(serviceSettings?.readOnly)}
            classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
          />
        </Grid>
      )}
      {!serviceSettings?.useServicePercent && (
        <Grid item xs={7}>
          <CustomTextField
            className={classes.customTextField}
            fullWidth
            isNumeric={true}
            startAdornment={<Typography variant="h6">{currencyCode}</Typography>}
            onChange={serviceLens.serviceAmount |> set |> onNumericTextFieldChange}
            label={
              (serviceLens.serviceTypeName |> get) +
              " | " +
              t("DECIMAL2", { number: serviceLens?.servicePercent |> get }) +
              " " +
              t("General.Percent") +
              " (" +
              currencyCode +
              " " +
              t("DECIMAL2", { number: serviceLens?.serviceAmount |> get }) +
              ")"
            }
            error={!isValid(serviceValidation?.serviceAmount)}
            helperText={getErrors(serviceValidation.serviceAmount)}
            value={serviceLens?.serviceAmount |> get}
            disabled={Boolean(serviceSettings?.readOnly)}
          />
        </Grid>
      )}
      <Grid item xs={1}>
        <IconButton size="small" onClick={handleDeleteService}>
          <DeleteSweepRoundedIcon />
        </IconButton>
      </Grid>
      <Grid item xs={4}>
        <ButtonGroup className={classes.rightAlign}>
          <Button
            id="service-percent"
            variant="contained"
            color={serviceSettings?.useServicePercent ? "primary" : "secondary"}
            onClick={handleUsePercent(true)}
          >
            {t("General.Percent")}
          </Button>
          <Button
            id="service-abs"
            variant="contained"
            color={!serviceSettings?.useServicePercent ? "primary" : "secondary"}
            onClick={handleUsePercent(false)}
          >
            {t("General.Abs")}
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
}

ServiceComponent.propTypes = {
  serviceLens: PropTypes.object.isRequired,
  serviceValidation: PropTypes.object.isRequired,
  settingsLens: PropTypes.object.isRequired,
  currencyCode: PropTypes.string.isRequired,
  onDeleteService: PropTypes.func.isRequired,
};

export default ServiceComponent;
